import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Globalny breakpoint hook + Provider — single matchMedia listener dla
 * całej apki (zamiast N osobnych useBreakpoint per komponent).
 *
 * Kanon breakpointów (Tailwind defaults):
 *   default = <640px    — telefon portrait
 *   sm:     >=640px     — telefon landscape, mały tablet
 *   md:     >=768px     — tablet
 *   lg:     >=1024px    — desktop (sidebar wraca)
 *   xl:     >=1280px    — wide desktop
 *   2xl:    >=1536px    — ultra-wide
 *
 * Synchroniczna initial value (window.innerWidth na mount) — nigdy
 * nie zwraca undefined, co eliminuje flash empty content.
 *
 * Debounce 100ms na resize żeby NIE rerender'ować apki przy każdym
 * piksu drag'u okna.
 */

export type Breakpoint = 'default' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const BREAKPOINTS: Array<{ name: Breakpoint; minWidth: number }> = [
  { name: '2xl', minWidth: 1536 },
  { name: 'xl', minWidth: 1280 },
  { name: 'lg', minWidth: 1024 },
  { name: 'md', minWidth: 768 },
  { name: 'sm', minWidth: 640 },
  { name: 'default', minWidth: 0 },
];

const ORDER: Record<Breakpoint, number> = {
  default: 0,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
  '2xl': 5,
};

function resolveBreakpoint(width: number): Breakpoint {
  for (const bp of BREAKPOINTS) {
    if (width >= bp.minWidth) return bp.name;
  }
  return 'default';
}

function getInitialWidth(): number {
  // SSR-safe (Vite SPA pierwszy render i tak w przegladarce, ale defensywnie).
  if (typeof window === 'undefined') return 1024; // default desktop fallback
  return window.innerWidth;
}

const BreakpointContext = createContext<Breakpoint>(resolveBreakpoint(getInitialWidth()));

export function BreakpointProvider({ children }: { children: ReactNode }) {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() =>
    resolveBreakpoint(getInitialWidth()),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const onResize = () => {
      // Debounce 100ms — uniknij rerender per-pixel drag okna
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const next = resolveBreakpoint(window.innerWidth);
        // setState z fn-form żeby React zrobił bail-out gdy nic sie nie zmienilo
        setBreakpoint((prev) => (prev === next ? prev : next));
      }, 100);
    };

    window.addEventListener('resize', onResize);
    // Initial sync (na wypadek gdyby pierwsze getInitialWidth bylo SSR-stale)
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, []);

  return <BreakpointContext.Provider value={breakpoint}>{children}</BreakpointContext.Provider>;
}

/** Aktualny breakpoint (`'default'` ... `'2xl'`). */
export function useBreakpoint(): Breakpoint {
  return useContext(BreakpointContext);
}

/**
 * Helper — czy aktualny breakpoint jest >= podanego.
 * Użycie: `useIsAtLeast('md')` → true gdy >=768px.
 */
export function useIsAtLeast(target: Breakpoint): boolean {
  const current = useBreakpoint();
  return useMemo(() => ORDER[current] >= ORDER[target], [current, target]);
}

/**
 * Helper — czy aktualny breakpoint jest < podanego.
 * Użycie: `useIsBelow('md')` → true gdy <768px (mobile/small tablet).
 */
export function useIsBelow(target: Breakpoint): boolean {
  const current = useBreakpoint();
  return useMemo(() => ORDER[current] < ORDER[target], [current, target]);
}
